// NOTICE: KAPTION-INTEGRATION
// ctwa-ad-header-badge.DATA.1..4, MEDIA.1..2
// Click-to-WhatsApp ad attribution as stored by the backend
// (whatsapp-ctwa-referral): conversation.additional_attributes.ctwa_referral
// (first ad touch) / ctwa_last_referral (latest) and, per message,
// content_attributes.referral. These fields describe the ad creative Meta
// sent with the chat — never the campaign or ad set name.

const pick = (source, snake, camel) => source?.[snake] ?? source?.[camel];

const normalize = raw => {
  if (!raw || typeof raw !== 'object') return null;
  const referral = {
    sourceType: pick(raw, 'source_type', 'sourceType') || null,
    sourceId: pick(raw, 'source_id', 'sourceId') || null,
    sourceUrl: pick(raw, 'source_url', 'sourceUrl') || null,
    headline: pick(raw, 'headline', 'headline') || null,
    body: pick(raw, 'body', 'body') || null,
    mediaType: pick(raw, 'media_type', 'mediaType') || null,
    imageUrl: pick(raw, 'image_url', 'imageUrl') || null,
    videoUrl: pick(raw, 'video_url', 'videoUrl') || null,
    thumbnailUrl: pick(raw, 'thumbnail_url', 'thumbnailUrl') || null,
    hasClickId: Boolean(pick(raw, 'ctwa_clid', 'ctwaClid')),
    welcomeMessage: pick(raw, 'welcome_message', 'welcomeMessage') || null,
    capturedAt: pick(raw, 'captured_at', 'capturedAt') || null,
  };
  const hasContent = [
    referral.sourceId,
    referral.sourceUrl,
    referral.headline,
    referral.body,
  ].some(Boolean);
  return hasContent ? referral : null;
};

const additionalAttributesOf = chat =>
  chat?.additional_attributes || chat?.additionalAttributes || {};

const messageReferral = messages => {
  const withReferral = (messages || []).filter(
    message =>
      (message?.content_attributes || message?.contentAttributes)?.referral
  );
  if (!withReferral.length) return { first: null, last: null };
  const referralOf = message =>
    normalize(
      (message.content_attributes || message.contentAttributes).referral
    );
  return {
    first: referralOf(withReferral[0]),
    last: referralOf(withReferral[withReferral.length - 1]),
  };
};

// ctwa-ad-header-badge.DATA.1 / .DATA.2 - conversation attributes win; the
// dashboard's conversation-metadata record (fresh on open) and the loaded
// messages cover conversations whose list entry predates the capture.
export const resolveCtwaReferral = ({ chat, metadata, messages } = {}) => {
  const sources = [
    additionalAttributesOf(chat),
    additionalAttributesOf(metadata),
  ];
  const fromAttributes = key =>
    sources.map(attrs => normalize(attrs?.[key])).find(Boolean) || null;

  const fromMessages = messageReferral(messages || chat?.messages);
  const first = fromAttributes('ctwa_referral') || fromMessages.first;
  const last =
    fromAttributes('ctwa_last_referral') || fromMessages.last || first;
  if (!first && !last) return null;

  return {
    first: first || last,
    last,
    // ctwa-ad-header-badge.DATA.3
    hasDifferentFirstTouch: Boolean(
      first && last && first.sourceId !== last.sourceId
    ),
  };
};

// ctwa-ad-header-badge.DATA.4 - where the ad was shown, derived from the
// source link Meta sends (fb.me, instagram.com/p/…, wa.me/wamo/status/…).
export const ctwaPlacement = sourceUrl => {
  if (!sourceUrl) return null;
  let host;
  let path;
  try {
    ({ host, pathname: path } = new URL(sourceUrl));
  } catch {
    return null;
  }
  host = host.replace(/^www\./, '');
  if (host === 'wa.me' && path.startsWith('/wamo/status')) {
    return 'WHATSAPP_STATUS';
  }
  if (host === 'instagram.com' || host.endsWith('.instagram.com')) {
    return 'INSTAGRAM';
  }
  if (
    ['fb.me', 'facebook.com', 'm.facebook.com', 'fb.com'].includes(host) ||
    host.endsWith('.facebook.com')
  ) {
    return 'FACEBOOK';
  }
  return null;
};

// ctwa-ad-header-badge.MEDIA.1 - Meta CDN media links are signed and carry
// their expiry as a hex unix timestamp in the `oe` query parameter.
export const isMetaMediaUrlExpired = (url, now = Date.now()) => {
  if (!url) return true;
  let expiresHex;
  try {
    expiresHex = new URL(url).searchParams.get('oe');
  } catch {
    return true;
  }
  if (!expiresHex || !/^[0-9a-f]+$/i.test(expiresHex)) return false;
  return parseInt(expiresHex, 16) * 1000 <= now;
};

// ctwa-ad-header-badge.MEDIA.2 - the preview image to try, or null.
export const ctwaPreviewImage = (referral, now = Date.now()) => {
  const candidate = referral?.imageUrl || referral?.thumbnailUrl;
  if (!candidate || isMetaMediaUrlExpired(candidate, now)) return null;
  return candidate;
};
